import React, { useState } from "react";
import styled from "styled-components";
import { Trash2, Plus } from "lucide-react";
import { useParams } from "react-router-dom";
import { create_product_excess } from "services/apiSrv";
import { useQueryClient } from 'react-query';
import { show_toast } from "utils/helpers";

const Container = styled.div`
  background: #fff;
  padding: 28px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  max-width: 100%;
  margin: 20px auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const Title = styled.h3`
  margin: 0 0 32px 0;
  color: #001f5f;
  font-size: 24px;
  font-weight: 600;
`;

const Section = styled.div`
  margin-bottom: 36px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
  &:last-of-type { border-bottom: none; }
`;

const SectionTitle = styled.h4`
  margin: 0 0 18px 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
  background: #f8fafc;
  border-radius: 10px;
  overflow: hidden;
  border: 1.5px solid #e2e8f0;
`;

const Th = styled.th`
  background: #e0e7ff;
  padding: 14px;
  text-align: left;
  font-weight: 600;
  color: #1e40af;
  font-size: 14px;
`;

const Td = styled.td`
  padding: 14px;
  border-top: 1px solid #e2e8f0;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 11px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;


const TextArea = styled.textarea`
  min-width: 100%;
  padding: 11px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;           /* ← matches surrounding text better */
  line-height: 1.5;               /* ← more comfortable for multi-line reading */
  min-height: 96px;               /* ← gives it a decent starting size (~4–5 lines) */
  resize: vertical;               /* ← most common choice — allows height change only */
  background-color: white;
  color: #111827;

  &::placeholder {
    color: #9ca3af;
  }

  &:hover {
    border-color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }

  &:disabled {
    background-color: #f3f4f6;
    color: #6b7280;
    cursor: not-allowed;
    border-color: #d1d5db;
  }

  /* Optional: better scrollbar appearance (modern browsers) */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 11px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  background: white;
  font-size: 14px;
`;

const Button = styled.button`
  background: ${props => props.primary ? '#3b82f6' : '#10b981'};
  color: white;
  border: none;
  padding: 11px 18px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  transition: all 0.2s;
  &:hover {
    background: ${props => props.primary ? '#2563eb' : '#059669'};
  }
`;

const DeleteBtn = styled.button`
  background: #fee2e2;
  color: #dc2626;
  border: none;
  width: 34px;
  height: 34px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: #fecaca; }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1.5fr auto;
  gap: 16px;
  align-items: end;
  margin-bottom: 20px;
`;

const ExcessConfigurationForm = () => {
  const { id: prodSysemRef } = useParams();
  const queryClient = useQueryClient();

  // Standard Excess
  const [standardRateType, setStandardRateType] = useState("percentage");
  const [standardRateValue, setStandardRateValue] = useState(10);
  const [minExcess, setMinExcess] = useState(1000);
  const [maxExcess, setMaxExcess] = useState(5000);
  const [simpleExcessValue, setSimpleExcessValue] = useState("");
  const [simpleExcesses, setSimpleExcesses] = useState([]);


  // === Customer Excess Options ===
  const [optionLabel, setOptionLabel] = useState("");
  const [optionType, setOptionType] = useState("percentage");
  const [optionValue, setOptionValue] = useState("");

  const [savedOptions, setSavedOptions] = useState([]); // This will be shown in table

  // === Additional Rules ===
  const [ruleCondition, setRuleCondition] = useState("");
  const [rulePercent, setRulePercent] = useState("");
  const [savedRules, setSavedRules] = useState([]);

  // Add Option
  const addOption = () => {
    if (!optionLabel.trim() || !optionValue) return alert("Please fill Label and Value");

    const newOption = {
      id: Date.now(),
      label: optionLabel,
      type: optionType,
      value: Number(optionValue)
    };

    setSavedOptions([...savedOptions, newOption]);

    // Clear inputs
    setOptionLabel("");
    setOptionType("percentage");
    setOptionValue("");
  };

  // Add Rule
  const addRule = () => {
    if (!ruleCondition.trim() || !rulePercent) return alert("Please fill condition and percentage");

    const newRule = {
      id: Date.now(),
      condition: ruleCondition,
      additionalExcessPercent: Number(rulePercent)
    };

    setSavedRules([...savedRules, newRule]);

    // Clear inputs
    setRuleCondition("");
    setRulePercent("");
  };

  const addSimpleExcess = () => {
    const val = simpleExcessValue.trim();
    if (!val || !String(val)) return alert("Enter a valid excess");
    setSimpleExcesses([
      ...simpleExcesses,
      { id: Date.now(), value: String(val) }
    ]);
    setSimpleExcessValue("");
  };

  const deleteExcess = (id) => setSimpleExcesses(simpleExcesses.filter(o => o.id !== id));
  const deleteOption = (id) => setSavedOptions(savedOptions.filter(o => o.id !== id));
  const deleteRule = (id) => setSavedRules(savedRules.filter(r => r.id !== id));

  const handleSave = async () => {
    const payload = {
      productSystemRef: prodSysemRef,
      productExcess: {
        excess: simpleExcesses.map(item => item.value),
        standard: {
          rate: {
            rateType: standardRateType,
            rateValue: standardRateType === "percentage"
              ? Number(standardRateValue) 
              : Number(standardRateValue)
          },
          minimum: Number(minExcess) || 0,
          maximum: Number(maxExcess) || null
        },
        options: savedOptions.map(opt => ({
          label: opt.label,
          value: opt.type === "percentage"
            ? opt.value 
            : opt.value
        })),
        additionalRules: savedRules.map(rule => ({
          condition: rule.condition,
          additionalExcessPercent: rule.additionalExcessPercent
        }))
      }
    };

    try {
      await create_product_excess(payload);
      queryClient.invalidateQueries(['productDetails', prodSysemRef]);
      show_toast(`Excess configuration saved successfully!`, "success");
    } catch (err) {
      console.error(err);
      show_toast(`Failed to configure excess`, "error");
    }
  };

  return (
    <Container>
      <Title>Configure Product Excess</Title>

      {/* Standard Excess */}
      <Section>
        <SectionTitle>Standard Excess</SectionTitle>
        <Row style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
          <div>
            <label className="block mb-2 font-semibold text-sm">Type</label>
            <Select value={standardRateType} onChange={e => setStandardRateType(e.target.value)}>
              <option value="percentage">Percentage of Premium</option>
              <option value="fixed">Fixed Amount</option>
            </Select>
          </div>
          <div>
            <label className="block mb-2 font-semibold text-sm">Value</label>
            <Input type="number" value={standardRateValue} onChange={e => setStandardRateValue(e.target.value)} />
          </div>
          <div>
            <label className="block mb-2 font-semibold text-sm">Min Amount</label>
            <Input type="number" value={minExcess} onChange={e => setMinExcess(e.target.value)} />
          </div>
          <div>
            <label className="block mb-2 font-semibold text-sm">Max Amount</label>
            <Input type="number" value={maxExcess} onChange={e => setMaxExcess(e.target.value)} />
          </div>
        </Row>
      </Section>

      {/* Customer Excess Options */}
      <Section>
        <SectionTitle>Voluntary Excess</SectionTitle>

        {/* Single Input Row */}
        <Row>
          <div>
            <label className="block mb-2 font-semibold text-sm">Label</label>
            <Input
              value={optionLabel}
              onChange={e => setOptionLabel(e.target.value)}
              placeholder="e.g. Lower Excess"
            />
          </div>
          <div>
            <label className="block mb-2 font-semibold text-sm">Type</label>
            <Select value={optionType} onChange={e => setOptionType(e.target.value)}>
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed Amount</option>
            </Select>
          </div>
          <div>
            <label className="block mb-2 font-semibold text-sm">Value</label>
            <Input
              type="number"
              value={optionValue}
              onChange={e => setOptionValue(e.target.value)}
              placeholder={optionType === "percentage" ? "e.g. 5" : "e.g. 2500"}
            />
          </div>
          <div style={{ alignSelf: 'end' }}>
            <Button primary onClick={addOption}>
              <Plus size={16} /> Add
            </Button>
          </div>
        </Row>

        {/* Results Table */}
        {savedOptions.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>Label</Th>
                <Th>Type</Th>
                <Th>Value</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {savedOptions.map(opt => (
                <tr key={opt.id}>
                  <Td>{opt.label}</Td>
                  <Td>{opt.type === "percentage" ? "Percentage" : "Fixed Amount"}</Td>
                  <Td>{opt.value} {opt.type === "percentage" ? "%" : ""}</Td>
                  <Td>
                    <DeleteBtn onClick={() => deleteOption(opt.id)}>
                      <Trash2 size={16} />
                    </DeleteBtn>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      <Section>
        <SectionTitle>Excess</SectionTitle>

        {/* Single Input Row */}
        <Row>
          <div>
            <label className="block mb-2 font-semibold text-sm">Excess Description</label>
            <TextArea
              type="text"
              value={simpleExcessValue}
              onChange={e => setSimpleExcessValue(e.target.value)}
              placeholder="Enter description here..."
            />
          </div>

        </Row>
            <div style={{ alignSelf: 'end' }}>
            <Button primary onClick={addSimpleExcess}>
              <Plus size={16} /> Add
            </Button>
          </div>

        {/* Results Table */}
        {simpleExcesses.map(opt => (
          <tr key={opt.id}>
            <Td>{opt.value}</Td>
            <Td>
              <DeleteBtn onClick={() => deleteExcess(opt.id)}>
                <Trash2 size={16} />
              </DeleteBtn>
            </Td>
          </tr>
        ))}
      </Section>

      {/* Additional Rules */}
      <Section>
        <SectionTitle>Additional Excess Rules</SectionTitle>

        <Row style={{ gridTemplateColumns: '3fr 1.5fr auto' }}>
          <div>
            <label className="block mb-2 font-semibold text-sm">Condition</label>
            <Input
              value={ruleCondition}
              onChange={e => setRuleCondition(e.target.value)}
              placeholder="e.g. driver.age < 25"
            />
          </div>
          <div>
            <label className="block mb-2 font-semibold text-sm">Additional Excess (%)</label>
            <Input
              type="number"
              value={rulePercent}
              onChange={e => setRulePercent(e.target.value)}
            />
          </div>
          <div style={{ alignSelf: 'end' }}>
            <Button primary onClick={addRule}>
              <Plus size={16} /> Add
            </Button>
          </div>
        </Row>

        {savedRules.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th style={{ width: '70%' }}>Condition</Th>
                <Th>Extra Excess (%)</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {savedRules.map(rule => (
                <tr key={rule.id}>
                  <Td>{rule.condition}</Td>
                  <Td>{rule.additionalExcessPercent}%</Td>
                  <Td>
                    <DeleteBtn onClick={() => deleteRule(rule.id)}>
                      <Trash2 size={16} />
                    </DeleteBtn>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      <div style={{ textAlign: 'right', paddingTop: '30px' }}>
        <Button primary onClick={handleSave} style={{ padding: '14px 32px', fontSize: '16px' }}>
          Save Excess Configuration
        </Button>
      </div>
    </Container>
  );
};

export default ExcessConfigurationForm;